import { chartPointAtPixel, validAnchors } from "./curveDigitizer";

export function pixelAtLoad(anchors, load) {
  const ordered = [...anchors].sort((a, b) => a.value - b.value);
  for (let i = 1; i < ordered.length; i += 1) {
    const a = ordered[i - 1], b = ordered[i];
    if (load >= a.value && load <= b.value) return a.pixel + (load - a.value) / (b.value - a.value) * (b.pixel - a.pixel);
  }
  return null;
}

export function missingTraceLoads(targets, points) {
  return targets.filter(load => !points.some(point => Math.abs(point.load - load) < 0.000001));
}

/** Trace only observed pixels connected column-by-column to the selected line.
 * No gap filling, extrapolation, smoothing or inference of axis scales. All
 * coordinates refer to the original raster, never the zoomed display. */
export function traceCurveColor({ data, width, height }, xAnchors, yAnchors, seed, tolerance = 45) {
  const empty = { points: [], color: null };
  if (!validAnchors(xAnchors) || !validAnchors(yAnchors) || !width || !height ||
      !chartPointAtPixel(xAnchors, yAnchors, seed.x, seed.y)) return empty;
  const sx = Math.min(width - 1, Math.floor(seed.x * width));
  const sy = Math.min(height - 1, Math.floor(seed.y * height));
  const offset = (sy * width + sx) * 4;
  const color = Array.from(data.slice(offset, offset + 3));
  // White/transparent background is not a curve. Neutral dark lines are
  // allowed, but intersections with axes/grid lines can stop the trace.
  if (data[offset + 3] < 200 || Math.min(...color) > 235) return empty;
  const limit = Math.max(5, Math.min(100, Number(tolerance) || 45));
  const left = Math.max(0, Math.ceil(Math.min(...xAnchors.map(a => a.pixel)) * width));
  const right = Math.min(width - 1, Math.floor(Math.max(...xAnchors.map(a => a.pixel)) * width));
  const top = Math.max(0, Math.ceil(Math.min(...yAnchors.map(a => a.pixel)) * height));
  const bottom = Math.min(height - 1, Math.floor(Math.max(...yAnchors.map(a => a.pixel)) * height - 0.5));
  const matches = (x, y) => {
    const i = (y * width + x) * 4;
    return data[i + 3] >= 200 && Math.hypot(data[i] - color[0], data[i + 1] - color[1], data[i + 2] - color[2]) <= limit;
  };
  const runs = (x) => {
    const result = [];
    for (let y = top; y <= bottom; y += 1) {
      if (!matches(x, y)) continue;
      const start = y;
      while (y < bottom && matches(x, y + 1)) y += 1;
      result.push({ y: (start + y) / 2, size: y - start + 1, start, end: y });
    }
    return result;
  };
  const maxThickness = Math.max(8, Math.min(24, height * 0.02));
  const first = runs(sx).find(run => sy >= run.start && sy <= run.end && run.size <= maxThickness);
  if (!first) return empty;
  const walk = direction => {
    const path = [];
    let previous = first;
    for (let x = sx + direction; x >= left && x <= right; x += direction) {
      const last = previous;
      const nearby = runs(x).filter(run => Math.abs(run.y - last.y) <= Math.max(4, last.size * 2));
      // Stop at ambiguous branches, large jumps, grid intersections or gaps.
      if (nearby.length !== 1 || nearby[0].size > maxThickness) break;
      previous = nearby[0];
      path.push({ x, y: previous.y });
    }
    return path;
  };
  const path = [...walk(-1).reverse(), { x: sx, y: first.y }, ...walk(1)];
  if (path.length < 5) return empty;
  // Sample at explicit load values, not at raster bends. Otherwise antialiasing,
  // image resolution and unequally spaced X ticks change least-squares weights.
  // Every sampled Y still comes from an observed native column; no gap filling.
  const low = Math.min(...xAnchors.map(a => a.value));
  const high = Math.max(...xAnchors.map(a => a.value));
  const targets = [...new Set([...xAnchors.map(a => a.value), 5, 10, 20, 25, 30, 50, 75, 100])]
    .filter(load => load > 0 && load <= 100 && load >= low && load <= high).sort((a, b) => a - b);
  const columns = new Map(path.map(point => [point.x, point.y]));
  const points = targets.map(load => {
    const x = pixelAtLoad(xAnchors, load);
    const column = Math.min(width - 1, Math.floor(x * width + 1e-9));
    if (!columns.has(column)) return null;
    return chartPointAtPixel(xAnchors, yAnchors, x, (columns.get(column) + 0.5) / height);
  }).filter(Boolean);
  const coverage = [path[0], path[path.length - 1]].map(point => chartPointAtPixel(
    xAnchors, yAnchors, Math.min(Math.max(point.x / width, Math.min(...xAnchors.map(a => a.pixel))), Math.max(...xAnchors.map(a => a.pixel))),
    (point.y + 0.5) / height,
  )?.load);
  return { points, color, targets, coverage };
}
